import React, { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    invitationLocalStatusUiValues,
    invitationPermissionUiValues,
    LOCAL_INVITATION_STATUS,
} from '../../_constants';
import { LOCATION_PERMISSIONS } from '../../_constants/permissions.constants';
import { InvitationsHelper } from '../../_helpers';
import { InvitationFilter } from '../../_modelsTS/Pagination';
import {
    FilterCheckboxItem,
    HorizontalFilterDropdown,
} from '../../_components/HorizontalFilterDropdown';

type InvitationsFiltersProps = {
    onChangeFilters: (...args: any) => void;
    style?: CSSProperties;
    className?: string;
    filter: InvitationFilter;
    rooms?: { uid: string; name: string }[];
};

export function InvitationsFilters({
    onChangeFilters,
    filter,
    className = '',
    rooms = [],
}: InvitationsFiltersProps) {
    //LIBRARIES
    const { t } = useTranslation();

    const [permissions, setPermissions] = useState<any[]>([]);
    const [status, setStatus] = useState<any[]>([]);
    const [selectedRooms, setSelectedRooms] = useState<string[]>([]);

    useEffect(() => {
        if (filter?.status) {
            setStatus(filter.status);
        }
        if (filter?.permission_type) {
            setPermissions(filter.permission_type);
        }
        if (filter?.room_uid) {
            setSelectedRooms(filter.room_uid);
        }
    }, [filter]);

    const toggleRoom = (roomUid: string) => {
        let updatedRooms = selectedRooms;
        if (selectedRooms?.includes(roomUid)) {
            updatedRooms = updatedRooms.filter((uid) => uid !== roomUid);
        } else {
            updatedRooms = [...updatedRooms, roomUid];
        }

        setSelectedRooms(updatedRooms);
        onChangeFilters({
            ...filter,
            room_uid: updatedRooms,
        });
    };

    const togglePermission = (permissionOption: string | number) => {
        let updatedPermission = permissions;
        if (permissions?.includes(permissionOption)) {
            updatedPermission = updatedPermission.filter(
                (perm) => perm !== permissionOption,
            );
        } else {
            updatedPermission = [...updatedPermission, permissionOption];
        }

        setPermissions(updatedPermission);
        onChangeFilters({
            ...filter,
            permission_type: updatedPermission,
        });
    };

    const toggleSuperguest = () => {
        onChangeFilters({
            ...filter,
            is_superguest: !filter?.is_superguest ? true : false,
        });
    };

    const toggleStatus = (statusOption: string | number) => {
        let updatedStatus = status;
        if (status?.includes(statusOption)) {
            updatedStatus = updatedStatus.filter(
                (stat) => stat !== statusOption,
            );
        } else {
            updatedStatus = [...updatedStatus, statusOption];
        }

        setStatus(updatedStatus);
        onChangeFilters({
            ...filter,
            status: updatedStatus,
        });
    };

    const clearRooms = () => {
        setSelectedRooms([]);
        onChangeFilters({ ...filter, room_uid: [] });
    };

    const clearPermissions = () => {
        setPermissions([]);
        onChangeFilters({
            ...filter,
            permission_type: [],
            is_superguest: false,
        });
    };

    const clearStatus = () => {
        setStatus([]);
        onChangeFilters({ ...filter, status: [] });
    };

    const permissionsActiveCount =
        permissions.length + (filter?.is_superguest ? 1 : 0);

    //VIEW
    return (
        <div
            className={`${className} d-flex flex-wrap align-items-center`}
            style={{ gap: 12 }}
        >
            {rooms.length > 0 && (
                <HorizontalFilterDropdown
                    label={t('common.rooms')}
                    activeCount={selectedRooms.length}
                >
                    <FilterCheckboxItem
                        checked={selectedRooms.length === 0}
                        label={t('common.all')}
                        onClick={clearRooms}
                    />
                    {rooms.map((room) => (
                        <FilterCheckboxItem
                            key={room.uid}
                            checked={selectedRooms.includes(room.uid)}
                            label={room.name}
                            onClick={() => toggleRoom(room.uid)}
                        />
                    ))}
                </HorizontalFilterDropdown>
            )}

            <HorizontalFilterDropdown
                label={t('common.permission')}
                activeCount={permissionsActiveCount}
            >
                <FilterCheckboxItem
                    checked={permissionsActiveCount === 0}
                    label={t('common.all')}
                    onClick={clearPermissions}
                />
                {buildPermissionsOptions().map((option) => (
                    <FilterCheckboxItem
                        key={option.label}
                        checked={permissions.includes(option.value)}
                        label={option.label}
                        onClick={() => togglePermission(option.value)}
                    />
                ))}
                <FilterCheckboxItem
                    checked={!!filter?.is_superguest}
                    label={t('invitations.superguest')}
                    onClick={toggleSuperguest}
                />
            </HorizontalFilterDropdown>

            <HorizontalFilterDropdown
                label={t('common.status')}
                activeCount={status.length}
            >
                <FilterCheckboxItem
                    checked={status.length === 0}
                    label={t('common.all')}
                    onClick={clearStatus}
                />
                {buildStatusOptions().map((option) => (
                    <FilterCheckboxItem
                        key={option.label}
                        checked={status.includes(option.value)}
                        label={option.label}
                        onClick={() => toggleStatus(option.value)}
                    />
                ))}
            </HorizontalFilterDropdown>
        </div>
    );

    function buildPermissionsOptions() {
        const availablePermissions = [
            LOCATION_PERMISSIONS.ADMIN,
            LOCATION_PERMISSIONS.INSTALLER,
            LOCATION_PERMISSIONS.GUEST,
        ];

        return availablePermissions.map((permission) => ({
            label: invitationPermissionUiValues[permission],
            value: permission,
        }));
    }

    function buildStatusOptions() {
        return statusOptions().map((localStatus) => ({
            label: invitationLocalStatusUiValues[localStatus],
            value: InvitationsHelper.localStatusToQueryFilter(localStatus),
        }));

        function statusOptions() {
            return Object.keys(LOCAL_INVITATION_STATUS).filter(
                (status) =>
                    LOCAL_INVITATION_STATUS[status] !==
                    LOCAL_INVITATION_STATUS.UNDEFINED,
            );
        }
    }
}
